import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {boolean} */
  #altVariantFilterEnabled = false;

  connectedCallback() {
    super.connectedCallback();

    this.#altVariantFilterEnabled = this.dataset.altVariantFilter === 'true';

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.#altVariantFilterEnabled) {
      requestAnimationFrame(() => {
        this.#applyAltVariantFilter(this.#getSelectedColorValue());
        this.#setupFilteredSlideScrollSync();
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Applies alt-text filtering immediately when a variant option is selected.
   */
  #handleVariantSelected = () => {
    if (!this.#altVariantFilterEnabled) return;

    this.#applyAltVariantFilter(this.#getSelectedColorValue());
  };

  /**
   * Selects a slide by its original DOM index when alt-text filtering is active.
   *
   * @param {number} domIndex - The original slide index in the gallery.
   * @param {Event} event - The click event.
   */
  selectSlideByDomIndex(domIndex, event) {
    const slideshow = this.slideshow;
    const slide = slideshow?.refs?.slides?.[domIndex];

    if (!slideshow || !slide || slide.hasAttribute('data-alt-filter-hidden')) return;

    const visibleIndex = slideshow.slides?.indexOf(slide);

    if (visibleIndex == null || visibleIndex < 0) return;

    slideshow.select(visibleIndex, event);
  }

  /**
   * Keeps dot/thumbnail active states in sync after swipe or arrow navigation.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!this.querySelector('[data-alt-filter-hidden]')) return;

    requestAnimationFrame(() => {
      this.#syncControlSelection(event.detail?.slide);
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const slideshow = this.slideshow;
    const domIndex = event.detail.index;
    const slide = slideshow?.refs?.slides?.[domIndex];

    if (!slideshow || !slide || slide.hasAttribute('data-alt-filter-hidden')) return;

    const visibleIndex = slideshow.slides?.indexOf(slide);

    if (visibleIndex == null || visibleIndex < 0) return;

    slideshow.select(visibleIndex, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    const slide = this.slideshow?.refs?.slides?.[index];

    if (slide?.hasAttribute('data-alt-filter-hidden')) return;

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialog = this.refs.zoomDialogComponent;
    const zoomDialogMedia =
      zoomDialog?.refs?.media?.[index] ??
      zoomDialog?.querySelectorAll('.dialog-zoomed-gallery > li')?.[index];

    if (!zoomDialogMedia || !zoomDialog || zoomDialogMedia.hasAttribute('data-alt-filter-hidden')) return;

    zoomDialog.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * @returns {string | null}
   */
  #getSelectedColorValue() {
    const section = this.closest('.shopify-section, dialog');
    const variantPicker = section?.querySelector('variant-picker[data-template-product-match="true"]');

    if (variantPicker) {
      const colorFieldset = variantPicker.querySelector('fieldset.variant-option--image-swatches');
      const checked = colorFieldset?.querySelector('input:checked');

      if (checked instanceof HTMLInputElement && checked.value) {
        return checked.value;
      }

      const colorOptionIndex = this.dataset.colorOptionIndex;

      if (colorOptionIndex != null && colorOptionIndex !== '') {
        const variantScript = variantPicker.querySelector('script[type="application/json"]');

        if (variantScript?.textContent) {
          try {
            const variant = JSON.parse(variantScript.textContent);
            return variant.options?.[parseInt(colorOptionIndex, 10)] ?? null;
          } catch {
            return null;
          }
        }
      }
    }

    return null;
  }

  /**
   * @param {string | null | undefined} value
   * @returns {string}
   */
  #normalizeAlt(value) {
    return value?.trim().toLowerCase() ?? '';
  }

  /**
   * @param {string | null | undefined} alt
   * @param {string | null | undefined} colorValue
   * @returns {boolean}
   */
  #altMatchesColor(alt, colorValue) {
    const normalizedAlt = this.#normalizeAlt(alt);
    const normalizedColor = this.#normalizeAlt(colorValue);

    if (!normalizedAlt || !normalizedColor) return false;

    return (normalizedAlt === normalizedColor || (this.dataset.groupedColourGallery === "true" && normalizedAlt.split(" - ").at(-2) === normalizedColor));
  }

  /**
   * @param {Element} element
   * @returns {string}
   */
  #getMediaAlt(element) {
    const img = element.querySelector('.product-media__image, .deferred-media__poster-image, img');
    return img?.getAttribute('alt') ?? '';
  }

  /**
   * @param {Element} element
   * @param {boolean} hide
   */
  #setAltFilterHidden(element, hide) {
    if (hide) {
      element.setAttribute('hidden', '');
      element.setAttribute('data-alt-filter-hidden', 'true');

      if (element.tagName === 'SLIDESHOW-SLIDE') {
        element.setAttribute('aria-hidden', 'true');
      }

      return;
    }

    element.removeAttribute('hidden');
    element.removeAttribute('data-alt-filter-hidden');
  }

  #clearAltVariantFilter() {
    this.querySelectorAll('[data-alt-filter-hidden]').forEach((element) => {
      this.#setAltFilterHidden(element, false);
    });

    this.querySelectorAll('[data-original-on-click]').forEach((button) => {
      const original = button.getAttribute('data-original-on-click');

      if (original) {
        button.setAttribute('on:click', original);
      }

      button.removeAttribute('data-original-on-click');
      button.removeAttribute('data-slide-dom-index');
      button.removeAttribute('hidden');
      button.closest('li')?.removeAttribute('hidden');
    });
  }

  /**
   * @param {Element[]} elements
   * @param {string} colorValue
   */
  #hideNonMatchingElements(elements, colorValue) {
    elements.forEach((element) => {
      if (this.#altMatchesColor(this.#getMediaAlt(element), colorValue)) return;

      this.#setAltFilterHidden(element, true);
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getActiveFilteredSlide(activeSlide) {
    if (activeSlide instanceof HTMLElement && !activeSlide.hasAttribute('data-alt-filter-hidden')) {
      return activeSlide;
    }

    const slideshow = this.slideshow;
    const visibleSlides = slideshow?.slides;
    const scroller = slideshow?.refs?.scroller;

    if (!visibleSlides?.length) return null;

    if (!scroller || visibleSlides.length === 1) {
      return visibleSlides[slideshow.current] ?? visibleSlides[0] ?? null;
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;

    let bestSlide = visibleSlides[0];
    let bestDistance = Infinity;

    for (const slide of visibleSlides) {
      const slideRect = slide.getBoundingClientRect();

      if (slideRect.width === 0 && slideRect.height === 0) continue;

      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - scrollerCenter);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestSlide = slide;
      }
    }

    return bestSlide ?? null;
  }

  #setupFilteredSlideScrollSync() {
    const scroller = this.slideshow?.refs?.scroller;

    if (!scroller || scroller.dataset.altFilterScrollSync === 'true') return;

    scroller.dataset.altFilterScrollSync = 'true';

    scroller.addEventListener(
      'scroll',
      () => {
        if (!this.querySelector('[data-alt-filter-hidden]')) return;

        requestAnimationFrame(() => {
          this.#syncControlSelection();
        });
      },
      { passive: true, signal: this.#controller.signal }
    );
  }

  /**
   * Keeps slideshow dots/thumbnails aligned with filtered slides.
   */
  #syncSlideshowControls() {
    const slideshow = this.slideshow;
    const allSlides = slideshow?.refs?.slides;

    if (!slideshow || !allSlides?.length) return;

    this.querySelectorAll('slideshow-controls').forEach((controls) => {
      const buttons = controls.querySelectorAll(
        'button[on\\:click^="/select/"], button[on\\:click*="selectSlideByDomIndex/"]'
      );

      buttons.forEach((button, domIndex) => {
        const slide = allSlides[domIndex];
        const isHidden = slide?.hasAttribute('data-alt-filter-hidden') ?? false;

        button.toggleAttribute('hidden', isHidden);
        button.closest('li')?.toggleAttribute('hidden', isHidden);

        if (!(button instanceof HTMLButtonElement) || isHidden || !slide) return;

        if (!button.hasAttribute('data-original-on-click')) {
          const originalOnClick = button.getAttribute('on:click') ?? '';
          button.setAttribute('data-original-on-click', originalOnClick);
        }

        if (this.dataset.groupedColourGallery === 'true') {
          const visibleSlides = Array.from(allSlides).filter(item => !item.hasAttribute('data-alt-filter-hidden'));
          const visibleIndex = visibleSlides.indexOf(slide) + 1;
          button.setAttribute('aria-label', `Slide ${visibleIndex} of ${visibleSlides.length}`);
          if (button.getAttribute('ref') === 'dots[]') button.textContent = String(visibleIndex);
        }

        button.setAttribute('data-slide-dom-index', String(domIndex));
        button.setAttribute('on:click', `media-gallery/selectSlideByDomIndex/${domIndex}`);
      });
    });

    this.#syncControlSelection();
  }

  /**
   * @param {HTMLElement | undefined | null} [activeSlide]
   */
  #syncControlSelection(activeSlide) {
    const slideshow = this.slideshow;
    const allSlides = slideshow?.refs?.slides;

    if (!slideshow || !allSlides?.length) return;

    const currentSlide = this.#getActiveFilteredSlide(activeSlide ?? null);
    const currentDomIndex = currentSlide ? allSlides.indexOf(currentSlide) : -1;

    this.querySelectorAll('slideshow-controls').forEach((controls) => {
      const buttons = controls.querySelectorAll(
        'button[on\\:click^="/select/"], button[on\\:click*="selectSlideByDomIndex/"]'
      );

      buttons.forEach((button) => {
        let domIndex = parseInt(button.getAttribute('data-slide-dom-index') ?? '', 10);

        if (Number.isNaN(domIndex)) {
          const match = button.getAttribute('on:click')?.match(/selectSlideByDomIndex\/(\d+)/);
          domIndex = match ? parseInt(match[1], 10) : -1;
        }

        button.setAttribute('aria-selected', `${domIndex === currentDomIndex}`);
      });
    });
  }

  #refreshSlideshowAfterFilter() {
    const slideshow = this.slideshow;
    const visibleSlides = slideshow?.slides;

    if (!slideshow || !visibleSlides?.length) return;

    visibleSlides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== 0}`);
    });

    slideshow.select(0, undefined, { animate: false });

    requestAnimationFrame(() => {
      this.#syncControlSelection();
    });
  }

  /**
   * @param {string | null} colorValue
   */
  #applyAltVariantFilter(colorValue) {
    if (!this.#altVariantFilterEnabled) return;

    this.#clearAltVariantFilter();

    if (!colorValue) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const slideMatchCount = slides.filter((slide) =>
      this.#altMatchesColor(this.#getMediaAlt(slide), colorValue)
    ).length;

    if (slideMatchCount === 0) return;

    this.#hideNonMatchingElements(slides, colorValue);
    this.#hideNonMatchingElements(Array.from(this.querySelectorAll('.media-gallery__grid > li')), colorValue);

    const zoomDialog = this.refs.zoomDialogComponent;

    if (zoomDialog) {
      this.#hideNonMatchingElements(
        Array.from(zoomDialog.querySelectorAll('.dialog-zoomed-gallery > li')),
        colorValue
      );
      this.#hideNonMatchingElements(
        Array.from(zoomDialog.querySelectorAll('.dialog-thumbnails-list__thumbnail')),
        colorValue
      );
    }

    this.#syncSlideshowControls();
    this.#setupFilteredSlideScrollSync();

    requestAnimationFrame(() => {
      this.#refreshSlideshowAfterFilter();
    });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
